import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, parseISO, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDate = v => { try { return v ? format(parseISO(v), 'd MMM yyyy') : '–'; } catch { return v || '–'; } };
const fmtCurrency = v => v != null ? `$${parseFloat(v).toFixed(2)}` : '$0.00';
const today       = new Date().toISOString().split('T')[0];
const thirtyAgo   = subDays(new Date(), 30).toISOString().split('T')[0];

const COLS = [
  { key: 'client_name',   label: 'Client Name' },
  { key: 'ndis_number',   label: 'NDIS Number',    render: v => v || '–' },
  { key: 'fund_name',     label: 'Fund Name',       render: v => v || '–' },
  { key: 'fund_type',     label: 'Fund Type',       render: v => v ? v.replace(/_/g, ' ') : '–' },
  { key: 'plan_start',    label: 'Plan Start',      render: fmtDate },
  { key: 'plan_end',      label: 'Plan End',        render: fmtDate },
  { key: 'fund_amount',   label: 'Budget ($)',      render: fmtCurrency },
  { key: 'spent_amount',  label: 'Spent ($)',       render: fmtCurrency },
  {
    key: '_remaining',
    label: 'Remaining ($)',
    render: (v, row) => {
      const rem = parseFloat(row.fund_amount || 0) - parseFloat(row.spent_amount || 0);
      return <span className={rem < 0 ? 'text-red-600 font-bold' : 'text-green-600'}>${rem.toFixed(2)}</span>;
    },
  },
  {
    key: '_utilisation',
    label: 'Utilisation (%)',
    render: (v, row) => {
      const pct = row.fund_amount > 0 ? ((row.spent_amount / row.fund_amount) * 100).toFixed(1) : 0;
      const cls = pct > 90 ? 'text-red-600 font-bold' : pct > 70 ? 'text-amber-600' : 'text-green-600';
      return <span className={cls}>{pct}%</span>;
    },
  },
  { key: 'shifts_in_period', label: 'Shifts in Period', render: v => v || 0 },
];

export default function PlanManagedFunding() {
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]         = useState(thirtyAgo);
  const [endDate, setEnd]             = useState(today);
  const [pendingStart, setPendingStart] = useState(thirtyAgo);
  const [pendingEnd, setPendingEnd]     = useState(today);

  const pagination = usePagination(data);

  const load = useCallback((sd, ed) => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: sd, end_date: ed });
    api.get(`/reports/plan-managed-funding?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(thirtyAgo, today); }, [load]);

  const handleApply = () => {
    setStart(pendingStart);
    setEnd(pendingEnd);
    load(pendingStart, pendingEnd);
  };

  const summary = useMemo(() => ({
    clients:   new Set(data.map(r => r.client_name)).size,
    budget:    data.reduce((s, r) => s + parseFloat(r.fund_amount  || 0), 0),
    spent:     data.reduce((s, r) => s + parseFloat(r.spent_amount || 0), 0),
    remaining: data.reduce((s, r) => s + (parseFloat(r.fund_amount || 0) - parseFloat(r.spent_amount || 0)), 0),
  }), [data]);

  const chips = [{ label: 'Date Range', value: `${startDate} — ${endDate}` }];

  return (
    <ReportLayout
      title="Plan Managed Funding"
      data={data}
      filename="plan_managed_funding"
      columns={COLS}
      activeChips={chips}
      showFiltersPanel={showFilters}
      onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={pendingStart} onChange={e => setPendingStart(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={pendingEnd} onChange={e => setPendingEnd(e.target.value)} className="input-field !w-auto text-sm" />
          </div>
          <div className="flex items-end">
            <button onClick={handleApply} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-4 gap-4 mb-4">
        {[
          { label: 'Total Clients',    value: summary.clients,                                                                   color: 'text-slate-900' },
          { label: 'Total Budget',     value: `$${summary.budget.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`,        color: 'text-blue-600'  },
          { label: 'Total Spent',      value: `$${summary.spent.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`,         color: 'text-amber-600' },
          { label: 'Total Remaining',  value: `$${summary.remaining.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`,     color: summary.remaining < 0 ? 'text-red-600' : 'text-green-600' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="text-xs text-slate-500 mb-1">{card.label}</div>
            <div className={clsx('text-2xl font-bold', card.color)}>{card.value}</div>
          </div>
        ))}
      </div>
      <ReportTable
        columns={COLS}
        data={data}
        loading={loading}
        pagination={pagination}
        emptyMsg="No funding records found."
      />
    </ReportLayout>
  );
}
